import { Box, Stack, Typography, useTheme } from "@mui/material";
import { ScrapEyesEvent } from "components/scrapEyeLabelling/types";
import { formatAreaShort } from "components/identifyEventsTool/utils/formatArea";
import { Dayjs } from "dayjs";
import { useMemo } from "react";
import { useFormatNumber } from "src/utils/formatNumber";
import { parseTrackCarArea, pickTrackCarColor } from "src/utils/trackCarColor";
import { useUnitsFormatter } from "hooks/formatters";

interface VideoTimelineAreaTrackProps {
  events: ScrapEyesEvent[];
  eventType: "drop" | "lift";
  videoStart: Dayjs;
  duration: number;
}

const AreaLabelSegment = ({
  colors,
  number,
  segment,
}: {
  colors: string[];
  number: number;
  segment: string;
}) => (
  <Box
    component="span"
    sx={{
      backgroundColor: pickTrackCarColor(colors, number),
      color: "text.primary",
    }}
  >
    {formatAreaShort(segment)}
  </Box>
);

export const VideoTimelineAreaTrack = ({
  events,
  eventType,
  videoStart,
  duration,
}: VideoTimelineAreaTrackProps) => {
  const formatNumber = useFormatNumber()(2, false);
  const formatUnits = useUnitsFormatter(false);
  const theme = useTheme();
  const filteredEvents = useMemo(
    () => events.filter((e) => e.eventType === eventType && e.area),
    [events, eventType]
  );
  const hasAnyMass = useMemo(
    () => filteredEvents.some((e) => e.mass),
    [filteredEvents]
  );

  return (
    <Box
      sx={{
        position: "relative",
        height: hasAnyMass ? 18 : 14,
        width: "100%",
      }}
    >
      {filteredEvents.map((event) => {
        const startSeconds = event.start.diff(videoStart, "second");
        const endSeconds = event.end.diff(videoStart, "second");
        const eventDuration = Math.max(
          endSeconds - startSeconds,
          (0.5 / 100) * duration
        );
        const midpointPercent =
          ((startSeconds + eventDuration / 2) / duration) * 100;

        const formattedMass = event.mass
          ? `${formatNumber(Number(event.mass))}${formatUnits("mass")}`
          : undefined;

        const area = event.area ?? "";
        const trackCar = parseTrackCarArea(area);

        return (
          <Stack
            key={event.id}
            spacing={0}
            sx={{
              position: "absolute",
              left: `${midpointPercent}%`,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                transform: "translateX(-50%)",
                fontWeight: "bold",
                color: "text.secondary",
                fontSize: "0.6rem",
                whiteSpace: "nowrap",
                lineHeight: 1,
                pt: formattedMass ? 0.1 : undefined,
              }}
            >
              {trackCar ? (
                <>
                  <AreaLabelSegment
                    colors={theme.palette.categorical.cool}
                    number={trackCar.trackNumber}
                    segment={trackCar.trackSegment}
                  />
                  <AreaLabelSegment
                    colors={theme.palette.categorical.warm}
                    number={trackCar.carNumber}
                    segment={trackCar.carSegment}
                  />
                </>
              ) : (
                event.areaShortLabel
              )}
            </Typography>
            {formattedMass ? (
              <Typography
                variant="caption"
                sx={{
                  transform: "translateX(-50%)",
                  fontStyle: "italic",
                  color: "text.secondary",
                  fontSize: "0.4rem",
                  whiteSpace: "nowrap",
                  lineHeight: 1,
                }}
              >
                {formattedMass}
              </Typography>
            ) : null}
          </Stack>
        );
      })}
    </Box>
  );
};
